/**
 * MissedSlaDialog — Allows marking a study with missed SLA reasons.
 *
 * Provides a list of SLA reason categories and a notes field,
 * then calls api.markMissedSla(objKey, reasons, note).
 */
import { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  Alert,
  Divider,
  Box,
} from '@mui/material';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';

/** Common SLA miss reason categories matching GWT MissedSlaPanel */
const SLA_CATEGORIES = [
  {
    category: 'Physician Delay',
    reasons: ['Late dictation', 'Proofreading delay', 'Addendum required', 'Case complexity'],
  },
  {
    category: 'Technical',
    reasons: ['System downtime', 'Image quality', 'Prior comparison needed', 'Network issue'],
  },
  {
    category: 'Administrative',
    reasons: ['Missing information', 'Insurance verification', 'Scheduling conflict'],
  },
  {
    category: 'Volume',
    reasons: ['High volume', 'Staffing shortage', 'Emergency cases prioritized'],
  },
];

interface MissedSlaDialogProps {
  open: boolean;
  studies: Study[];
  onClose: () => void;
  onComplete?: () => void;
}

export const MissedSlaDialog = ({
  open,
  studies,
  onClose,
  onComplete,
}: MissedSlaDialogProps) => {
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const toggleReason = (category: string, reason: string) => {
    setSelected((prev) => {
      const next = { ...prev };
      const catSet = new Set(next[category] ?? []);
      if (catSet.has(reason)) catSet.delete(reason);
      else catSet.add(reason);
      next[category] = catSet;
      return next;
    });
  };

  const hasSelection = Object.values(selected).some((s) => s.size > 0);

  const handleSubmit = async () => {
    setPending(true);
    setError(null);
    try {
      const reasons = Object.entries(selected)
        .filter(([, s]) => s.size > 0)
        .map(([category, s]) => ({ category, reasons: [...s] }));

      for (const s of studies) {
        await api.markMissedSla(s.objKey, reasons, note);
      }
      onComplete?.();
      onClose();
      // Reset form
      setSelected({});
      setNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  const handleClose = () => {
    setSelected({});
    setNote('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Missed SLA</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select reasons for the SLA miss on {studies.length} study(ies):
        </Typography>
        {SLA_CATEGORIES.map((cat, idx) => (
          <Box key={cat.category}>
            {idx > 0 && <Divider sx={{ my: 1 }} />}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              {cat.category}
            </Typography>
            <FormGroup>
              {cat.reasons.map((r) => (
                <FormControlLabel
                  key={r}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected[cat.category]?.has(r) ?? false}
                      onChange={() => toggleReason(cat.category, r)}
                    />
                  }
                  label={r}
                />
              ))}
            </FormGroup>
          </Box>
        ))}
        <TextField
          label="Notes"
          multiline
          minRows={2}
          fullWidth
          sx={{ mt: 2 }}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={pending || !hasSelection}
        >
          {pending ? 'Saving…' : 'Mark Missed SLA'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
